/**
 * Remote device ops module wordbook (remote ops console single page +
 * register-detail remote-ops section, shared prefix pages.deviceOps.*;
 * menu keys in menu.ts). Status/rule texts map to backend
 * model/deviceops.go constants and plan §3.5/§5.2. Four-page merge
 * 2026-10-14: the old command center / device logs / database ops pages
 * are offline; command.* wording lives on via the console ledger drawer
 * (LedgerDrawer) and write confirmation (ConfirmModal); db.write.*
 * keeps only the entries the console result rendering still uses.
 */
export default {
  // ===== Shared =====
  'pages.deviceOps.deviceSelect.placeholder':
    'Search and pick a device (name/code/SN/tenant/store)',
  'pages.deviceOps.deviceSelect.tenant': 'tenant',
  'pages.deviceOps.deviceSelect.store': 'store',
  'pages.deviceOps.deviceSelect.selectedFallback': 'Selected device',
  'pages.deviceOps.type.DEVICE_STATE': 'Device state snapshot',
  'pages.deviceOps.type.LOG_LIST': 'Log file list',
  'pages.deviceOps.type.LOG_QUERY': 'Log filtered query',
  'pages.deviceOps.type.DB_TABLES': 'Database table list',
  'pages.deviceOps.type.DB_READ': 'Database read-only query',
  'pages.deviceOps.type.DB_BACKUP': 'Database backup',
  'pages.deviceOps.type.DB_WRITE': 'Database controlled write',
  'pages.deviceOps.type.SIGN_RESET': 'Sign credential reset',
  'pages.deviceOps.risk.READ': 'Read',
  'pages.deviceOps.risk.BACKUP': 'Backup',
  'pages.deviceOps.risk.WRITE': 'Write',
  'pages.deviceOps.risk.MEDIUM': 'Medium (touches local secure data)',
  'pages.deviceOps.status.PENDING_CONFIRM': 'Pending confirm',
  'pages.deviceOps.status.CREATED': 'Queued',
  'pages.deviceOps.status.DELIVERED': 'Claimed by device',
  'pages.deviceOps.status.EXECUTING': 'Executing',
  'pages.deviceOps.status.DONE': 'Done',
  'pages.deviceOps.status.FAILED': 'Failed',
  'pages.deviceOps.status.FAILED_DELIVERY':
    'Delivery failed (retries exhausted)',
  'pages.deviceOps.status.FAILED_TIMEOUT': 'Timed out',
  'pages.deviceOps.status.REJECTED_ROW_LIMIT': 'Row limit hit, rolled back',
  'pages.deviceOps.status.REJECTED_VALIDATION':
    'Rejected by validation (audited)',
  'pages.deviceOps.status.SKIPPED_DEVICE_BUSY': 'Device busy, deferred',
  'pages.deviceOps.status.CANCELED': 'Canceled',
  'pages.deviceOps.status.EXPIRED': 'Expired',
  'pages.deviceOps.rejectRule.R-SQL-MULTI': 'Multiple statements',
  'pages.deviceOps.rejectRule.R-SQL-TYPE': 'Statement type not allowed',
  'pages.deviceOps.rejectRule.R-SQL-TABLE': 'Table not in whitelist',
  'pages.deviceOps.rejectRule.R-SQL-NOWHERE': 'Missing WHERE clause',
  'pages.deviceOps.rejectRule.R-SQL-PRAGMA': 'Forbidden family (PRAGMA/DDL…)',
  'pages.deviceOps.rejectRule.R-SQL-PARAM': 'Placeholder/params invalid',
  'pages.deviceOps.rejectRule.R-SQL-LIMIT': 'LIMIT over cap',
  // Device register detail remote-ops section
  'pages.deviceOps.detail.sectionTitle': 'Remote diagnostics',
  'pages.deviceOps.detail.onlineStatus': 'Online status',
  'pages.deviceOps.detail.online': 'Online',
  'pages.deviceOps.detail.offline': 'Offline',
  'pages.deviceOps.detail.lastOnlineTime': 'Last heartbeat',
  'pages.deviceOps.detail.pendingTitle': 'In-flight commands',

  // ===== Command ledger & confirmation (former command center wordbook, reused by console LedgerDrawer/ConfirmModal) =====
  'pages.deviceOps.command.title': 'Remote Ops Command Center',
  'pages.deviceOps.command.confirm.title': 'Write second confirmation',
  'pages.deviceOps.command.confirm.ok': 'Confirm & dispatch',
  'pages.deviceOps.command.confirm.cancel': 'Cancel',
  'pages.deviceOps.command.confirm.success':
    'Confirmed; command queued for the device to pull',
  'pages.deviceOps.command.confirm.conflict':
    'Command state changed (maybe confirmed/canceled already); refresh the list',
  'pages.deviceOps.command.confirm.riskTitle':
    'High risk: this directly mutates the device database',
  'pages.deviceOps.command.confirm.riskDesc':
    'The device takes a triple-file backup (PPOS.db/-wal/-shm) before executing; if the hit count exceeds the cap it rolls back and does not write. Verify the normalized SQL and params below word by word.',
  'pages.deviceOps.command.confirm.expectMaxRows': 'Affected-rows cap',
  'pages.deviceOps.command.confirm.sqlTitle':
    'Normalized SQL (what you see is what executes)',
  'pages.deviceOps.command.confirm.paramsTitle': 'Params',
  'pages.deviceOps.command.confirm.auditNote':
    'The confirmation and sqlHash are written to the audit ledger; after confirmation nobody can alter the payload.',
  'pages.deviceOps.command.confirm.cooldown':
    'Safety cooldown: confirm in {seconds}s',
  'pages.deviceOps.command.cancel.success': 'Command canceled',
  'pages.deviceOps.command.cancel.conflict':
    'Command already dispatched or terminal; cannot cancel',
  'pages.deviceOps.command.cancel.confirmTitle': 'Cancel this command?',
  'pages.deviceOps.command.cancel.confirmDesc':
    'Only undispatched (pending/queued) commands can be canceled; the cancel action is audited too.',
  'pages.deviceOps.command.action.detail': 'Detail',
  'pages.deviceOps.command.action.confirm': 'Confirm',
  'pages.deviceOps.command.action.cancel': 'Cancel',
  'pages.deviceOps.command.column.commandNo': 'Command No.',
  'pages.deviceOps.command.column.deviceCode': 'Device code',
  'pages.deviceOps.command.column.deviceCodePlaceholder': 'Exact match',
  'pages.deviceOps.command.column.deviceId': 'Device ID',
  'pages.deviceOps.command.column.tenant': 'Tenant',
  'pages.deviceOps.command.column.store': 'Store',
  'pages.deviceOps.command.column.type': 'Type',
  'pages.deviceOps.command.column.typePlaceholder': 'Select type',
  'pages.deviceOps.command.column.risk': 'Risk',
  'pages.deviceOps.command.column.status': 'Status',
  'pages.deviceOps.command.column.statusPlaceholder': 'Select status',
  'pages.deviceOps.command.column.rejectRule': 'Reject rule',
  'pages.deviceOps.command.column.rowsAffected': 'Rows affected',
  'pages.deviceOps.command.column.createdByName': 'Created by',
  'pages.deviceOps.command.column.createdReason': 'Reason',
  'pages.deviceOps.command.column.createdAt': 'Created at',
  'pages.deviceOps.command.column.createdAtRange': 'Created range',
  'pages.deviceOps.command.column.deliveredAt': 'Delivered at',
  'pages.deviceOps.command.column.finishedAt': 'Finished at',
  'pages.deviceOps.command.column.action': 'Actions',
  'pages.deviceOps.command.detail.title': 'Command detail',
  'pages.deviceOps.command.detail.expectMaxRows': 'Affected-rows cap',
  'pages.deviceOps.command.detail.timeoutSeconds': 'Timeout (s)',
  'pages.deviceOps.command.detail.tryCount': 'Delivery count',
  'pages.deviceOps.command.detail.redeliverCount': 'Redelivery count',
  'pages.deviceOps.command.detail.rowsAffected': 'Rows affected',
  'pages.deviceOps.command.detail.payloadTitle':
    'Payload (persisted at creation; immutable after confirm)',
  'pages.deviceOps.command.detail.sqlTitle': 'Normalized SQL & hash',
  'pages.deviceOps.command.detail.deviceMetaTitle': 'Device-reported meta',
  'pages.deviceOps.command.detail.resultsTitle': 'Result chunks',
  'pages.deviceOps.command.detail.rowsAffectedShort': 'Rows',
  'pages.deviceOps.command.detail.downloadResult': 'Download',
  'pages.deviceOps.command.detail.noResults':
    'No result chunks yet (device has not reported, or this command has no payload)',
  'pages.deviceOps.command.detail.refreshHint':
    'Command not terminal; reopen the detail drawer to refresh progress.',
  'pages.deviceOps.command.detail.auditNote':
    'Every create/confirm/cancel here is written to the operation log and command ledger, retained for 180 days.',

  // ===== Database ops (former ops-page wordbook; only console-used entries kept: result rendering / over-cap rollback / confirm hints) =====
  'pages.deviceOps.db.write.audit.expectMaxRowsNote':
    'device rolls back over cap',
  'pages.deviceOps.db.write.rowLimitTitle': 'Device refused: row cap exceeded',
  'pages.deviceOps.db.write.rowLimitDesc':
    'The device counted {actual} matching rows, over the cap {limit}; it rolled back entirely and wrote nothing. Re-issue with a higher cap after manual review if appropriate.',
  'pages.deviceOps.db.write.result.backup': 'Backup file',
  'pages.deviceOps.db.write.result.finishedAt': 'Finished at',
  'pages.deviceOps.db.write.result.tookMs': 'Took',
  'pages.deviceOps.db.write.result.countMatched': 'Rows check',

  // ===== Remote ops console =====
  'pages.deviceOps.console.title': 'Ops Console',
  'pages.deviceOps.console.subtitle':
    'Single-device command-line ops window: commands dispatch serially (input stays open while busy — new lines queue up), with status/results/audit refs kept per entry; reuses the remote diagnosis command channel, zero backend changes',
  'pages.deviceOps.console.device': 'Target device',
  'pages.deviceOps.console.deviceOnlineOnly': 'Online',
  'pages.deviceOps.console.pendingOthers':
    '{count} in-flight from other sources',
  'pages.deviceOps.console.transcript.empty':
    'Empty session: type help to list commands; commands dispatch in order and results render under each entry',
  'pages.deviceOps.console.input.placeholder':
    'Type a command (e.g. help / state / tables / log query --since 1h / SELECT …), Ctrl+Enter to run',
  'pages.deviceOps.console.input.run': 'Run',
  'pages.deviceOps.console.sessionDark.toggle': 'Dark session theme',
  'pages.deviceOps.console.fullscreen.enter': 'Fullscreen',
  'pages.deviceOps.console.fullscreen.exit': 'Exit fullscreen',
  'pages.deviceOps.console.input.queued': '{count} queued',
  'pages.deviceOps.console.status.queued': 'Queued',
  'pages.deviceOps.console.status.waitingSecs': 'waiting {seconds}s',
  'pages.deviceOps.console.status.rhythmBoost': 'boosted poll ≈{seconds}s',
  'pages.deviceOps.console.status.rhythmBoostPlain': 'boosted poll',
  'pages.deviceOps.console.status.wakeHint': 'waiting device poll',
  'pages.deviceOps.console.status.wakeHintTip':
    'Boost parameters reach the device only via its poll responses (no push channel): the device polls at its base interval, so this command is claimed on its next poll — the first command may wait several minutes. Once claimed, and for subsequent commands within the boost window, responses arrive in seconds.',
  'pages.deviceOps.console.status.rhythmDefault': 'default poll, up to ~5 min',
  'pages.deviceOps.console.entry.continued': 'continued',
  'pages.deviceOps.console.entry.systemPrefix': 'system:',
  'pages.deviceOps.console.entry.parseErrorPrefix': 'Cannot parse command:',
  'pages.deviceOps.console.help.title': 'Available commands (help)',
  'pages.deviceOps.console.help.body':
    'state · tables · logs —— device state snapshot / DB table list / log file list\nbackup · backup list —— take 3-file snapshot / list backups\nsign reset [re-enroll|reactivate] —— remote sign credential repair (defaults to re-enroll = re-enroll only; reactivate = full re-activation)\nls [tables|logs] —— alias: table list / log file list (defaults to tables)\ngrep <keyword> [filters] —— alias: log query --grep <keyword> (same flags)\ncat <error|biz>_YYYY-MM-DD.txt —— alias: view the log file of that date (defaults to that day window)\nlog query [--kind error|biz] [--level L] [--tag T] [--grep K] [--file F] [--limit 1~200] [--since 1h|6h|24h or --from/--to YYYY-MM-DDTHH:mm:ss (mutually exclusive)]\nSELECT … —— read-only query (server validates and injects LIMIT 500; no parameter placeholders)\nUPDATE/DELETE … —— controlled write (fixed-password confirm on submit; two-phase confirm with 30s cooldown after enqueue; INSERT/REPLACE/BEGIN transaction scripts go through the same write channel verbatim; Shift+Enter for new lines)\ncancel —— cancel current undelivered entry · clear —— clear session · help —— this help',
  'pages.deviceOps.console.help.writePwdNote':
    'UPDATE/DELETE passthrough: a fixed password confirmation is required on submit (frontend-only check)',
  'pages.deviceOps.console.msg.pickDevice': 'Select a device first',
  'pages.deviceOps.console.msg.createFailed': 'Failed to create command',
  'pages.deviceOps.console.msg.writePwdWrong': 'Incorrect password',
  'pages.deviceOps.console.writePwd.title': 'Controlled-write confirm',
  'pages.deviceOps.console.writePwd.tip':
    'You are about to dispatch an UPDATE/DELETE statement. Enter the fixed password to confirm (frontend-only check):',
  'pages.deviceOps.console.writePwd.ok': 'Confirm & dispatch',
  'pages.deviceOps.console.writePwd.placeholder': 'Password',
  'pages.deviceOps.console.msg.trackingTimeout':
    'Local tracking stopped after 5 minutes; check the latest status in "Command ledger"',
  'pages.deviceOps.console.cancel.nothing':
    'Nothing cancellable (only undelivered pending-confirm/queued commands can be canceled)',
  'pages.deviceOps.console.cancel.success': 'Command canceled',
  'pages.deviceOps.console.cancel.conflict':
    'Cancel failed: command already delivered or terminal',
  'pages.deviceOps.console.cancel.failed': 'Cancel failed: {reason}',
  'pages.deviceOps.console.clear.blocked':
    'Session still has in-flight/pending-confirm entries: cancel or wait for terminal before clearing',
  'pages.deviceOps.console.confirm.title':
    'Write pending confirmation (confirmable after 30s cooldown)',
  'pages.deviceOps.console.confirm.normalizedSql':
    'Normalized statement (what you see is what runs)',
  'pages.deviceOps.console.confirm.sqlHash': 'sqlHash',
  'pages.deviceOps.console.confirm.backupPath': 'Backup dir',
  'pages.deviceOps.console.confirm.expectMaxRows': 'Row-limit cap',
  'pages.deviceOps.console.confirm.ackBackup':
    'Read: the device will take a 3-file snapshot backup automatically before execution (OpsBackup/{commandNo}/)',
  'pages.deviceOps.console.confirm.ackRollback':
    'Accepted: over-limit hits roll back automatically; if executed, the effect cannot be undone remotely',
  'pages.deviceOps.console.confirm.submit': 'Confirm & execute',
  'pages.deviceOps.console.confirm.cooldown': 'cooldown {seconds}s',
  'pages.deviceOps.console.confirm.conflict':
    'Confirm conflict: command state changed, view refreshed',
  'pages.deviceOps.console.confirm.success':
    'Confirmed and queued, waiting for the device to pull',
  'pages.deviceOps.console.confirm.failed': 'Confirm failed: {reason}',
  'pages.deviceOps.console.confirm.rejectedTitle':
    'SQL validation rejected (recorded)',
  'pages.deviceOps.console.result.loading': 'Fetching result shards…',
  'pages.deviceOps.console.result.empty': 'No result payload',
  'pages.deviceOps.console.result.continue': 'Fetch next page',
  'pages.deviceOps.console.result.truncated':
    'This page was truncated (single-scan budget hit): narrow the time window and continue',
  'pages.deviceOps.console.reason.continuedSuffix': ' (cursor continuation)',
  // Structured result columns/summaries (2026-10-19 display rework: device-real envelopes)
  'pages.deviceOps.console.result.tableName': 'Table',
  'pages.deviceOps.console.result.typeCol': 'Type',
  'pages.deviceOps.console.result.typeTable': 'table',
  'pages.deviceOps.console.result.typeView': 'view',
  'pages.deviceOps.console.result.objectCount': '{count} objects',
  'pages.deviceOps.console.result.fileName': 'File',
  'pages.deviceOps.console.result.kindCol': 'Kind',
  'pages.deviceOps.console.result.dateCol': 'Date',
  'pages.deviceOps.console.result.sizeCol': 'Size',
  'pages.deviceOps.console.result.fileCount': '{count} files',
  'pages.deviceOps.console.result.retentionDays': 'kept {retentionDays} days',
  'pages.deviceOps.console.result.truncatedRows':
    'Result truncated by shard/row budget (first {rowsAffected} rows shown)',
  // Device state snapshot summary (DEVICE_STATE)
  'pages.deviceOps.console.state.appVersion': 'App version',
  'pages.deviceOps.console.state.generatedAt': 'Generated at',
  'pages.deviceOps.console.state.networkStatus': 'Network',
  'pages.deviceOps.console.state.diskFree': 'Disk free',
  'pages.deviceOps.console.state.dbSize': 'Local DB size',
  'pages.deviceOps.console.state.dbPath': 'DB path',
  'pages.deviceOps.console.state.activation': 'Activation snapshot',
  'pages.deviceOps.console.state.statErrorTitle': 'State collection failed',
  // DB backup summary/list (DB_BACKUP)
  'pages.deviceOps.console.backup.col.commandNo': 'Backup',
  'pages.deviceOps.console.backup.col.total': 'Total',
  'pages.deviceOps.console.backup.col.mtime': 'Modified',
  'pages.deviceOps.console.backup.col.quickCheck': 'Integrity',
  'pages.deviceOps.console.backup.keepSummary':
    'keep last {keep} · root {root}',
  'pages.deviceOps.console.backup.backupDir': 'Backup dir',
  'pages.deviceOps.console.backup.tripleBytes': 'Triple size',
  'pages.deviceOps.console.backup.quickCheck': 'Integrity',
  'pages.deviceOps.console.backup.copyMs': 'Copy time',
  'pages.deviceOps.console.backup.checkMs': 'Check time',
  'pages.deviceOps.console.backup.pruned': 'Pruned old',
  'pages.deviceOps.console.backup.prunedCount': '{count} sets',
  'pages.deviceOps.console.backup.prunedNone': 'none',
  // Sign reset summary (SIGN_RESET)
  'pages.deviceOps.console.signreset.mode': 'Mode',
  'pages.deviceOps.console.signreset.tookMs': 'Took',
  'pages.deviceOps.console.signreset.summary': 'Result',
  // ===== Temporary poll boost (plan §3.3.2; auto-boost on console + manual grant in register detail) =====
  'pages.deviceOps.boost.scene.console': 'ops console',
  'pages.deviceOps.boost.autoReason': 'Auto boost on workbench open ({scene})',
  'pages.deviceOps.boost.renewReason': 'Manual boost renewal ({scene})',
  'pages.deviceOps.boost.activeTag': 'Poll boost active',
  'pages.deviceOps.boost.intervalLabel': 'Interval',
  'pages.deviceOps.boost.remainingLabel': 'Remaining',
  'pages.deviceOps.boost.untilLabel': 'Until',
  'pages.deviceOps.boost.grantedByLabel': 'Granted by',
  'pages.deviceOps.boost.reasonLabel': 'Reason: ',
  'pages.deviceOps.boost.renew': 'Renew 10 min',
  'pages.deviceOps.boost.revoke': 'Release boost',
  'pages.deviceOps.boost.revokeConfirmTitle': 'Release boost for this device?',
  'pages.deviceOps.boost.revokeConfirmDesc':
    'The device falls back to the default interval on its next poll; your concurrent boost quota is released.',
  'pages.deviceOps.boost.revokeSuccess':
    'Released; the device returns to the default interval on its next poll',
  'pages.deviceOps.boost.revokeConflict':
    'No active boost (may have expired or been released)',
  'pages.deviceOps.boost.degradedTitle':
    'Boost limit reached, not auto-boosted',
  'pages.deviceOps.boost.degradedHint':
    'The device keeps polling at the default interval, so command latency may reach minutes; functionality is unaffected. Release one of your own active boosts in the panel below and retry.',
  'pages.deviceOps.boost.expiredTitle': 'Boost expired',
  'pages.deviceOps.boost.expiredHint':
    'The device has returned to the default poll interval; boost again if second-level responsiveness is needed.',
  'pages.deviceOps.boost.reBoost': 'Boost again',
  'pages.deviceOps.boost.grantSuccess':
    'Boost granted; takes effect on the device next poll (up to ~5 min at the default interval)',
  'pages.deviceOps.boost.auditNote':
    'Grant/renew/release are all written to the operation audit (operator/time/device/interval/duration/reason).',
  // Command center · active boosts panel (§3.3.2-③ one-click release)
  'pages.deviceOps.boost.panel.title': 'Active boosts',
  'pages.deviceOps.boost.panel.refresh': 'Refresh',
  'pages.deviceOps.boost.panel.empty': 'No active boost',
  'pages.deviceOps.boost.panel.device': 'Device',
  'pages.deviceOps.boost.panel.tenant': 'Tenant',
  'pages.deviceOps.boost.panel.store': 'Store',
  'pages.deviceOps.boost.panel.interval': 'Interval',
  'pages.deviceOps.boost.panel.remaining': 'Remaining',
  'pages.deviceOps.boost.panel.grantedBy': 'Granted by',
  'pages.deviceOps.boost.panel.grantedAt': 'Granted at',
  'pages.deviceOps.boost.panel.lastPollAt': 'Last poll',
  'pages.deviceOps.boost.panel.reason': 'Reason',
  'pages.deviceOps.boost.panel.action': 'Action',
  'pages.deviceOps.boost.panel.revoke': 'Release',
  'pages.deviceOps.boost.panel.others': 'By others',
  // Register detail · temporary poll boost (interval+duration+audited reason, remaining time display)
  'pages.deviceOps.detail.boost.action': 'Temporary poll boost',
  'pages.deviceOps.detail.boost.status': 'Poll boost',
  'pages.deviceOps.detail.boost.idle': 'Default interval',
  'pages.deviceOps.detail.boost.submit': 'Grant boost',
  'pages.deviceOps.detail.boost.cancel': 'Cancel',
  'pages.deviceOps.detail.boost.hint':
    'Temporarily raises the interval at which this device polls for ops commands: the device enters boost mode on its next poll (up to ~5 min at the default interval) and falls back automatically on expiry, no release needed. Grant/renew/release are fully audited.',
  'pages.deviceOps.detail.boost.interval': 'Poll interval',
  'pages.deviceOps.detail.boost.duration': 'Boost duration',
  'pages.deviceOps.detail.boost.5s': '5 s (recommended)',
  'pages.deviceOps.detail.boost.15s': '15 s',
  'pages.deviceOps.detail.boost.30s': '30 s',
  'pages.deviceOps.detail.boost.5m': '5 min',
  'pages.deviceOps.detail.boost.10m': '10 min (recommended)',
  'pages.deviceOps.detail.boost.15m': '15 min',
  'pages.deviceOps.detail.boost.30m': '30 min (cap)',
  'pages.deviceOps.detail.boost.reason': 'Reason',
  'pages.deviceOps.detail.boost.reasonRequired': 'Reason is required (audited)',
  'pages.deviceOps.detail.boost.reasonPlaceholder':
    'Required, ≤200 chars, kept for audit traceability',
  'pages.deviceOps.detail.boost.renewReason':
    'Manual boost renewal from device register detail',

  // ===== Console · command ledger drawer & log CSV export (four-page merge, 2026-10-14) =====
  'pages.deviceOps.console.ledger.open': 'Command ledger',
  'pages.deviceOps.console.ledger.title': 'Command ledger',
  'pages.deviceOps.console.ledger.allDevices': 'All devices',
  'pages.deviceOps.console.ledger.allDevicesHint':
    'Off = current device only; on = command ledger across all devices',
  'pages.deviceOps.console.result.exportCsv': 'Export CSV',

  // ===== Console · local DB rebuild export (2026-10-16; server-side PPOS.db rebuild for adb push restore) =====
  'pages.deviceOps.console.dbRebuild.open': 'Local DB rebuild export',
  'pages.deviceOps.console.dbRebuild.title': 'Local DB rebuild export',
  'pages.deviceOps.console.dbRebuild.intro':
    'The server rebuilds the device local DB into a PPOS.db export; a device with a running task cannot start another.',
  'pages.deviceOps.console.dbRebuild.device': 'Target device',
  'pages.deviceOps.console.dbRebuild.winRange':
    'Time window (store local wall clock)',
  'pages.deviceOps.console.dbRebuild.winRangeHint':
    'Optional; empty = full export; parsed as store-local (Manila) wall clock',
  'pages.deviceOps.console.dbRebuild.includeReceiptText':
    'Include receipt text',
  'pages.deviceOps.console.dbRebuild.includeReceiptTextHint':
    'Turn off to shrink the export file',
  'pages.deviceOps.console.dbRebuild.submit': 'Start rebuild',
  'pages.deviceOps.console.dbRebuild.status.RUNNING': 'Running',
  'pages.deviceOps.console.dbRebuild.status.SUCCESS': 'Succeeded',
  'pages.deviceOps.console.dbRebuild.status.FAILED': 'Failed',
  'pages.deviceOps.console.dbRebuild.current.title': 'Current task',
  'pages.deviceOps.console.dbRebuild.current.taskNo': 'Task No.',
  'pages.deviceOps.console.dbRebuild.current.polling': 'polling every 5s…',
  'pages.deviceOps.console.dbRebuild.current.fileSize': 'File size',
  'pages.deviceOps.console.dbRebuild.current.rowsStats': 'Row stats',
  'pages.deviceOps.console.dbRebuild.current.sha256': 'SHA256',
  'pages.deviceOps.console.dbRebuild.current.errorMsg': 'Failure reason',
  'pages.deviceOps.console.dbRebuild.download': 'Download rebuild file',
  'pages.deviceOps.console.dbRebuild.adb.title': 'adb restore guide',
  'pages.deviceOps.console.dbRebuild.adb.copy': 'Copy full command',
  'pages.deviceOps.console.dbRebuild.history.title': 'Task history',
  'pages.deviceOps.console.dbRebuild.column.taskNo': 'Task No.',
  'pages.deviceOps.console.dbRebuild.column.status': 'Status',
  'pages.deviceOps.console.dbRebuild.column.fileSize': 'File size',
  'pages.deviceOps.console.dbRebuild.column.sha256': 'SHA256',
  'pages.deviceOps.console.dbRebuild.column.createTime': 'Created at',
  'pages.deviceOps.console.dbRebuild.column.createBy': 'Created by',
  'pages.deviceOps.console.dbRebuild.column.action': 'Actions',
  'pages.deviceOps.console.dbRebuild.action.download': 'Download',
  'pages.deviceOps.console.dbRebuild.msg.pickDevice': 'Select a device first',
  'pages.deviceOps.console.dbRebuild.msg.created': 'Started; task is running',
  'pages.deviceOps.console.dbRebuild.msg.downloadFailed': 'Download failed',
  'pages.deviceOps.console.dbRebuild.msg.trackingTimeout':
    'Local tracking stopped after 5 minutes; check the latest status in task history',
};
